/** What P9 reads for one released round: the team, this and the previous round's report, the league as of that round, the catalog and the draft's rationales. */
import type { ContentCatalog, DraftView, ErrorBody, MarketView, RoundReport, TeamState } from "@bsim/contracts";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRoute, useRoutes } from "../api/context";

/** While a year is not yet in the released list the page asks again this often, whatever the stream does: "being prepared" never outlasts the release by more than this. */
export const PREPARING_RECHECK_MS = 5_000;

/** One released year's report with its round number (the fake and the real api answer by id, so the round comes from the released list, not the report). */
export interface YearReport { readonly roundNo: number; readonly report: RoundReport }

export type ResultsData =
  | { readonly status: "loading" }
  | { readonly status: "no-team" }
  | { readonly status: "preparing"; readonly catalog: ContentCatalog; readonly team: TeamState }
  | { readonly status: "error"; readonly error: ErrorBody["error"] }
  | {
      readonly status: "ready"; readonly userId: string; readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView;
      readonly report: RoundReport; readonly previous: RoundReport | null;
      /** Every released year up to this one, oldest first (the trend table and the share-price chart read them); a year still loading is absent, so these fill in after the page. */
      readonly years: readonly YearReport[];
      /** The league as of the end of each year 0 (SimStart) … this one, by round number; a round that has not answered is absent. */
      readonly marketThrough: ReadonlyMap<number, MarketView>;
      /** The round's submitted rationales; absent when the draft cannot be read (the section then omits itself). */
      readonly draft: DraftView | null;
    };

export function useResultsData(cohortId: string, roundNo: number): ResultsData {
  const me = useRoute("authMe", undefined, { staleTime: Infinity });
  const teamId = me.data?.principal.memberships.find((m) => m.cohortId === cohortId)?.teamId ?? null;
  const team = useRoute("teamState", teamId === null ? null : { teamId });
  const catalog = useRoute("contentCatalog", { cohortId }, { staleTime: Infinity });
  const released = team.data?.releasedRounds.find((r) => r.roundNo === roundNo);
  const before = team.data?.releasedRounds.find((r) => r.roundNo === roundNo - 1);
  const market = useRoute("publicMarket", released === undefined ? null : { cohortId }, { query: { throughRoundNo: String(roundNo) } });
  const report = useRoute("roundReport", released === undefined || teamId === null ? null : { teamId, roundId: released.roundId });
  const previous = useRoute("roundReport", before === undefined || teamId === null ? null : { teamId, roundId: before.roundId });
  const draft = useRoute("getDraft", released === undefined || teamId === null ? null : { teamId, roundId: released.roundId });

  // The years behind this one (same cache keys as `report` and `previous`) and the league through each round, 0 (SimStart) to this one.
  const shown = (team.data?.releasedRounds ?? []).filter((r) => r.roundNo <= roundNo);
  const yearQueries = useRoutes("roundReport", teamId === null ? [] : shown.map((r) => ({ teamId, roundId: r.roundId })));
  const throughRounds = released === undefined ? [] : [0, ...shown.map((r) => r.roundNo)];
  const marketQueries = useRoutes("publicMarket", throughRounds.map(() => ({ cohortId })), throughRounds.map((n) => ({ throughRoundNo: String(n) })));
  const years: YearReport[] = shown.flatMap((r, i) => { const q = yearQueries[i]?.data; return q === undefined ? [] : [{ roundNo: r.roundNo, report: q }]; });
  const marketThrough = new Map<number, MarketView>(throughRounds.flatMap((n, i) => { const q = marketQueries[i]?.data; return q === undefined ? [] : [[n, q] as const]; }));

  const queryClient = useQueryClient();
  const waiting = team.data !== undefined && released === undefined;
  useEffect(() => {
    if (!waiting) return undefined;
    const id = setInterval(() => { void queryClient.invalidateQueries({ queryKey: ["teamState"] }); }, PREPARING_RECHECK_MS);
    return () => clearInterval(id);
  }, [waiting, queryClient]);

  if (me.data !== undefined && teamId === null) return { status: "no-team" };
  const failed = [me, team, catalog, market, report, previous].find((q) => q.error !== null);
  if (failed?.error) return { status: "error", error: failed.error.result.error };
  if (me.data === undefined || team.data === undefined || catalog.data === undefined) return { status: "loading" };
  if (released === undefined) return { status: "preparing", catalog: catalog.data, team: team.data };
  if (market.data === undefined || report.data === undefined || (before !== undefined && previous.data === undefined)) return { status: "loading" };
  return {
    status: "ready", userId: me.data.principal.userId, team: team.data, catalog: catalog.data, market: market.data, report: report.data,
    previous: previous.data ?? null, draft: draft.data ?? null, years, marketThrough,
  };
}
